import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import pic from "../assets/images/privacy.jpg";

const Privacy = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const navigate = useNavigate();

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-white"
    >
      {/* Hero Section - Fixed */}
      <motion.div
        variants={itemVariants}
        className="relative h-96 md:h-[500px] w-full overflow-hidden"
      >
        {/* Background Image */}
        <img
          src={pic}
          alt="Privacy Policy Background"
          className="w-full h-full object-cover absolute inset-0"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60 z-10" />

        {/* Content */}
        <div className="absolute inset-0 z-20 flex flex-col justify-center items-center text-center px-4">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
          >
            Privacy Policy
          </motion.h1>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100px" }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="h-1 bg-yellow-500 mb-4"
          />
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-lg md:text-xl text-white max-w-2xl mx-auto"
          >
            Your privacy is important to Geocode. This policy explains how we handle your information.
          </motion.p>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        {/* Content Grid */}
        <motion.div
          variants={containerVariants}
          className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Information Collection */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">📝</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Information Collection</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We collect basic information from clients and students such as name, email, phone number, and payment details. For students, we may track class attendance and progress on assigned syllabus.
            </p>
          </motion.div>

          {/* Use of Information */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl text-white">⚙️</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Use of Information</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Information is used to provide services, communicate updates, process payments, monitor student progress, and improve our offerings. We do not sell personal information to third parties.
            </p>
          </motion.div>

          {/* Data Security */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">🔒</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Data Security</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We take reasonable measures to protect your information from unauthorized access, alteration, disclosure, or destruction. Our systems are regularly updated to maintain security.
            </p>
          </motion.div>

          {/* Third-Party Sharing */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl text-white">🌐</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Third-Party Sharing</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We may share limited data with trusted service providers (like payment gateways, hosting providers) only to provide services. No personal information will be sold or shared for marketing purposes.
            </p>
          </motion.div>

          {/* Student & Client Privacy */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">🎓👔</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Student & Client Privacy</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Student progress reports and project work will be confidential and only shared with the student and Geocode team. Client project details will remain private unless explicit permission is given for portfolio use.
            </p>
          </motion.div>

          {/* General Notes */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -5,
              transition: { duration: 0.3 }
            }}
            className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl text-white">📌</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">General Notes</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">•</span>
                <span>By using Geocode services, you agree to this privacy policy.</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">•</span>
                <span>Geocode reserves the right to update the privacy policy at any time.</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-500 mr-3 mt-1">•</span>
                <span>Continued use of services indicates acceptance of updated policies.</span>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Commitment Section */}
        <motion.div
          variants={itemVariants}
          className="max-w-4xl mx-auto mt-16 text-center"
        >
          <div className="p-8 md:p-12">
            <h3 className="text-3xl md:text-4xl font-bold text-yellow-500 mb-6">
              Protecting Your Privacy !
            </h3>
            <p className="text-lg md:text-xl mb-6">
              We are committed to ensuring the security and confidentiality of your personal information.
            </p>
          </div>
        </motion.div>

        {/* Footer Section */}
        <motion.div
          variants={itemVariants}
          className=" text-center mb-6 mx-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-black text-white p-8 rounded-2xl shadow-2xl"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-yellow-400">
              Need Further Assistance?
            </h3>
            <p className="text-gray-300 text-lg mb-6">
              Our team is here to help you understand our cookie policy. Don't hesitate to reach out if you have any questions.
            </p>



            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full px-4">
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 15px 30px -5px rgba(255,215,0,0.3)"
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/")}
                className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg transition-all duration-300"
              >
                Back to Home
              </motion.button>

              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 15px 30px -5px rgba(255,215,0,0.3)"
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/contact")}
                className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg transition-all duration-300"
              >
                Contact Us
              </motion.button>
            </div>

          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Privacy;